<script lang="ts" setup>
  import { useBreakpoints } from '@storefront/storefront';
  import type { Paginator } from '@othercommerce/domain';
  import { type FormContextInterface, setModelWithContext } from '@othercommerce/foundation';
  import type { ListOrderItemResource, OrderResource } from '@storefront/resources';

  interface Props {
    products: Paginator<ListOrderItemResource>;
    order?: OrderResource;
    complaint: string;
    back?: string;
  }

  defineProps<Props>();

  const breakpoints = useBreakpoints();

  function onChoose(item: any, ctx: FormContextInterface) {
    const current: { id: number; orderItemComponentId: number | null }[] = ctx.data.value.items || [];

    let next;
    // Normal item
    if (!item.orderItemComponentId) {
      const exists = current.some(i => i.id === item.id);
      next = exists ? current.filter(i => i.id !== item.id) : [...current, { id: item.id, orderItemComponentId: null }];
    } else {
      // Item with components (SET/FLEXIBLE)
      const exists = current.some(i => i.id === item.id && i.orderItemComponentId === item.orderItemComponentId);
      next = exists
        ? current.filter(i => !(i.id === item.id && i.orderItemComponentId === item.orderItemComponentId))
        : [...current, { id: item.id, orderItemComponentId: item.orderItemComponentId }];
    }

    setModelWithContext('items', ctx, next);
  }

  function isSelected(item: any, data: any) {
    return data.items.some((i: { id: number; orderItemComponentId: number | null }) => {
      // Normal item
      if (!item.orderItemComponentId) {
        return i.id === item.id;
      } else {
        // Item with components (SET/FLEXIBLE)
        return i.id === item.id && i.orderItemComponentId === item.orderItemComponentId;
      }
    });
  }
</script>

<template>
  <SfTile v-if="products.data.length > 0" class="account-products" large>
    <template v-if="breakpoints.lg" #title>
      <h1 class="account__heading">
        {{ $t('SHOP.COMPLAINT.HEADER.SELECT_PRODUCTS_TO_COMPLAIN') }}
      </h1>
    </template>
    <template #default>
      <FormController v-slot="{data, ctx}" :action="complaint" :data="{ items: [], order }" class="account-products__content" method="POST">
        <div class="account-products__buttons">
          <SfButton :disabled="data.items.length === 0" primary type="submit">
            {{ $t('SHOP.COMPLAINT.ACTION.CREATE_COMPLAINT') }}
          </SfButton>
          <SfButton v-if="back" :href="back" primary>
            {{ $t('SHOP.ACTION.CANCEL') }}
          </SfButton>
        </div>
        <SfListCard v-for="product in products.data"
          :selected="isSelected(product, data)" class="line"
          interactive large
          role="button"
          tabindex="0"
          @click="onChoose(product, ctx)"
          @keydown.space="onChoose(product, ctx)"
        >
          <template #image>
            <MediaPicture :photo="product.photo" sizes="140x140 2x, 70x70" />
          </template>
          <template #default>
            <div class="line__content">
              <h2 class="line__heading" v-html="product.variantTitle" />
              <p v-if="product.variantSku" class="line__property" v-html="$t('SHOP.COMPLAINT.LABEL.SKU', { code: product.variantSku })" />
              <p class="line__property" v-html="$t('SHOP.COMPLAINT.LABEL.ORDER_NR', { number: product.order.number })" />
              <p v-if="product.warrantyExpiresAt" class="line__property" v-html="$t('SHOP.COMPLAINT.LABEL.WARRANTY_EXPIRES_AT', { date: product.warrantyExpiresAt })" />
            </div>
          </template>
          <template #icons>
            <div v-if="product.regularGross && product.regularGross !== product.priceGross" class="line__discount">
              {{ $t('SHOP.COMPLAINT.LABEL.DISCOUNT') }}
              <SfFormattedPercentage :negative="false" :value="((Number(product.regularGross) - Number(product.priceGross)) / Number(product.regularGross)) * 100" />
            </div>
          </template>
          <template #actions>
            <div class="line__pricing">
              <div v-if="product.regularGross && product.regularGross !== product.priceGross" class="line__regular">
                <SfMoney :code="product.currencyCode" :decimals="product.decimals" :value="product.regularGross" />
              </div>
              <div class="line__price">
                <SfUnit :quantity="product.amount" :unit="product.stockUnit" />
                &times;
                <SfMoney :code="product.currencyCode" :decimals="product.decimals" :value="product.priceGross" />
              </div>
            </div>
          </template>
        </SfListCard>
      </FormController>
    </template>
  </SfTile>

  <SfTile v-else>
    <div class="account-products__buttons">
      <SfButton v-if="back" :href="back" primary>
        {{ $t('SHOP.ACTION.CANCEL') }}
      </SfButton>
    </div>
    <SfDataPlaceholder>
      <template v-if="order">
        {{ $t('SHOP.COMPLAINT.MESSAGE.NO_PRODUCTS_TO_COMPLAIN_IN_ORDER') }}
      </template>
      <template v-else>
        {{ $t('SHOP.COMPLAINT.MESSAGE.NO_PRODUCTS_TO_COMPLAIN') }}
      </template>
    </SfDataPlaceholder>
  </SfTile>
  <SfPagination :pagination="products" />
</template>

<style lang="scss" scoped>
  @use "@storefront/styles/mixins/media";

  :deep(.list-card--selected) {
    .line__property {
      color: var(--sf-list-card-color-selected);
    }
  }

  .line {
    &__heading {
      font-weight: var(--font-weight-bold);
      font-size: 1rem;
    }

    &__discount {
      font-weight: var(--font-weight-bold);
      font-size: 0.875rem;
    }

    &__property {
      font-size: 0.75rem;
      color: var(--secondary-color);
    }

    &__regular {
      color: var(--danger-color);
      font-size: 0.875rem;
      text-decoration: line-through;
    }

    &__price {
      white-space: nowrap;
    }

    :deep(.list-card__image) {
      flex: 0 0 70px;

      @include media.query(md) {
        flex: 0 0 45px;
      }

      picture, img {
        display: flex;
        aspect-ratio: 1/1;
      }
    }
  }

  .account-products {
    &__buttons {
      display: flex;
      justify-content: flex-end;
      gap: 1rem;
    }

    &__content {
      display: flex;
      flex-direction: column;
      row-gap: 1.25rem;
    }
  }

  @include media.query(lg) {
    .account-products {
      row-gap: 1rem;
    }
  }
</style>
